<script setup>
import { computed, ref, shallowRef, watch } from 'vue';
import moment from 'moment';
import { useStore } from 'vuex';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiDetailList from '@/components/ui/UiDetailList.vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiFormDialog from '@/components/ui/UiFormDialog.vue';
import UiPerson from '@/components/ui/UiPerson.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import { apiRequest } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useConfirm } from '@/composables/useConfirm';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { PREFECT_ROLE_CODES } from '@/constants/accounts';
import { formatDateTime, titleCase } from '@/utils/format';

useSessionGuard();

// What's listed follows the active role. A Subject Teacher or Adviser gets
// the meetings parents asked them for, about grades, and accepts, declines,
// or moves one to another date and time (which also accepts it). A Prefect
// gets the parent summons from discipline reports at their level, theirs and
// the other Prefects'; the parent answers those. The parent is always told.
// A Prefect also sees the counseling sessions scheduled on those reports:
// no parent is asked, so there's nothing to answer, but a Prefect can still
// cancel one (the advisers are told).
const store = useStore();
const isPrefect = PREFECT_ROLE_CODES.includes(store.getters.activeRoleCode);
const year = computed(() => store.getters.getFilterSelected);

const STATUS = {
  pending: { text: 'Waiting for you', variant: 'amber' },
  accepted: { text: 'Accepted', variant: 'green' },
  declined: { text: 'Declined', variant: 'secondary' },
  cancelled: { text: 'Cancelled', variant: 'red' },
};
// Meetings the Prefect of Discipline asked parents to: the parent answers.
const sentByMe = (row) => row.initiatedBy === 'school';
// Only the side that received the request can move it, and requestedSchedule
// keeps the time it had before: the parent moves the school's requests, and
// you move the parents'.
const movedText = (row) =>
  `${sentByMe(row) ? 'The parent moved it' : 'You moved it'} from ${formatDateTime(row.requestedSchedule)}`;
const isCounseling = (row) => row.kind === 'counseling';
const statusOf = (row) =>
  isCounseling(row) && row.status !== 'cancelled'
    ? { text: 'Scheduled', variant: 'green' }
    : sentByMe(row) && row.status === 'pending' ? { text: 'Waiting for the parent', variant: 'amber' } : STATUS[row.status] ?? STATUS.pending;
const isPast = (row) => moment(row.schedule).isBefore(moment());
const kindOf = (row) => {
  if (isCounseling(row)) return { text: 'Counseling · Discipline report', variant: 'gray' };
  return sentByMe(row) ? { text: 'Summon · Discipline report', variant: 'violet' } : { text: 'Grades', variant: 'blue' };
};
// Who it's about: a counseling row already lists the students by name.
const aboutText = (row) =>
  row.student_name ? `${isCounseling(row) ? 'Report about' : 'About'} ${titleCase(row.student_name)}` : '';
const myId = () => Number(store.state.user?.id);
// Another Prefect's summons says who sent it.
const senderText = (row) => {
  if (!sentByMe(row) || isCounseling(row)) return '';
  return Number(row.teacherID) === myId() || !row.sent_by ? 'You asked' : `Sent by ${titleCase(row.sent_by)}`;
};

const headers = [
  { title: isPrefect ? 'With' : 'Parent', key: 'name' },
  { title: 'Type', key: 'kind', sortable: false },
  { title: 'Purpose', key: 'purpose' },
  { title: 'Schedule', key: 'schedule' },
  { title: 'Status', key: 'status' },
];

// A Prefect never answers a request, so they have nothing "to answer".
const TABS = computed(() => [
  ...(isPrefect
    ? []
    : [{ id: 'pending', name: 'To answer', count: items.value.filter((r) => !sentByMe(r) && (r.status ?? 'pending') === 'pending' && !isPast(r)).length }]),
  { id: 'upcoming', name: 'Upcoming' },
  { id: 'cancelled', name: 'Cancelled' },
  { id: 'all', name: 'All' },
]);
// "Cancelled by the parent on Oct 6" / "Cancelled by you on Oct 6".
const cancelledByText = (row) => {
  const by = Number(row.cancelledBy);
  if (by === myId()) return 'you';
  return by === Number(row.parentID) ? 'the parent' : 'the Prefect';
};
const cancelledText = (row) =>
  row.status === 'cancelled'
    ? `Cancelled by ${cancelledByText(row)}${
        row.cancelledAt ? ` on ${moment(row.cancelledAt).format('MMM D')}` : ''
      }`
    : '';
const tab = shallowRef(isPrefect ? 'upcoming' : 'pending');

const { items, loading, error, load } = useListResource(async () => {
  const rows = await apiRequest('get', `/parent-records/getTeacherAppointment/${year.value}`);
  return (Array.isArray(rows) ? rows : []).map((r) => ({ ...r, name: titleCase(r.name), status: r.status || 'pending' }));
});
watch(year, load, { immediate: true });

const shown = computed(() => {
  const list = [...items.value].sort((a, b) => moment(a.schedule) - moment(b.schedule));
  if (tab.value === 'pending') return list.filter((r) => !sentByMe(r) && r.status === 'pending' && !isPast(r));
  // Confirmed meetings, plus requests I sent that the parent hasn't answered.
  if (tab.value === 'upcoming') return list.filter((r) => !isPast(r) && (r.status === 'accepted' || (sentByMe(r) && r.status === 'pending')));
  if (tab.value === 'cancelled') return list.filter((r) => r.status === 'cancelled').reverse();
  return list.reverse();
});
const isOpenRow = (row) => !['declined', 'cancelled'].includes(row.status) && !isPast(row);
// Only requests sent to me can be accepted, declined or moved by me.
const canAnswer = (row) => !sentByMe(row) && isOpenRow(row);

// --- View -----------------------------------------------------------------
const viewing = shallowRef(null);
const viewOpen = shallowRef(false);
const details = computed(() => {
  const v = viewing.value;
  if (!v) return [];
  return [
    { label: 'Purpose', value: v.purpose, span: 2 },
    { label: isCounseling(v) ? 'Reported student' : 'Student', value: titleCase(v.student_name) },
    { label: 'Schedule', value: formatDateTime(v.schedule) },
    ...(v.requestedSchedule ? [{ label: sentByMe(v) ? 'You asked for' : 'Parent asked for', value: formatDateTime(v.requestedSchedule) }] : []),
    { label: isCounseling(v) ? 'Scheduled on' : 'Requested', value: formatDateTime(v.created_at) },
    ...(v.responseNote ? [{ label: sentByMe(v) ? "Parent's note" : 'Your note', value: v.responseNote, span: 2 }] : []),
    ...(v.status === 'cancelled' ? [{ label: 'Cancelled', value: cancelledText(v), span: 2 }] : []),
  ];
});
function view(row) {
  viewing.value = row;
  viewOpen.value = true;
}

// --- Respond ----------------------------------------------------------------
const { busy, run } = useAsyncAction();
async function respond(row, body, success) {
  const ok = await run(() => apiRequest('patch', `/parent-records/appointments/${row.id}/respond`, body), {
    success,
    error: 'Could not update the appointment.',
  });
  if (ok) {
    viewOpen.value = false;
    declineOpen.value = false;
    moveOpen.value = false;
    load();
  }
  return ok;
}

function accept(row) {
  respond(row, { action: 'accept' }, `Accepted. ${row.name} has been told.`);
}

const answering = shallowRef(null);
const declineOpen = shallowRef(false);
const declineForm = ref({ note: '' });
const DECLINE_FIELDS = [
  {
    key: 'note',
    label: 'Message to the parent',
    type: 'textarea',
    description: 'Optional, e.g. a better time to reach you.',
  },
];
function openDecline(row) {
  answering.value = row;
  declineForm.value = { note: '' };
  declineOpen.value = true;
}

const moveOpen = shallowRef(false);
const moveForm = ref({ date: '', time: '', note: '' });
const today = () => moment().format('YYYY-MM-DD');
const MOVE_FIELDS = computed(() => [
  { key: 'date', label: 'New date', type: 'date', required: true, min: today() },
  { key: 'time', label: 'New time', type: 'time', required: true },
  { key: 'note', label: 'Message to the parent', type: 'textarea', span: 2, description: 'Optional, e.g. why the time changed.' },
]);
function openMove(row) {
  answering.value = row;
  const when = moment(row.schedule);
  moveForm.value = { date: when.format('YYYY-MM-DD'), time: when.format('HH:mm'), note: '' };
  moveOpen.value = true;
}

// An accepted meeting the teacher can no longer attend; the parent is told.
// A counseling session is cancelled on its report, and the advisers are told.
const { confirm } = useConfirm();
async function cancelMeeting(row) {
  const counseling = isCounseling(row);
  const ok = await confirm({
    title: counseling ? 'Cancel this counseling session?' : 'Cancel this meeting?',
    description: counseling
      ? `${row.name} on ${formatDateTime(row.schedule)}. Their advisers will be told, and the report goes back to For resolution.`
      : `${row.name} on ${formatDateTime(row.schedule)}. They will be told it's cancelled.`,
    confirmText: counseling ? 'Cancel counseling' : 'Cancel meeting',
    cancelText: 'Keep it',
    destructive: true,
  });
  if (!ok) return;
  const request = counseling
    ? () => apiRequest('patch', `/parent-records/reports/${row.reportID}/counseling/cancel`)
    : () => apiRequest('delete', `/parent-records/cancelAppointment/${row.id}`);
  const success = counseling ? 'Counseling cancelled. The adviser has been told.' : 'Meeting cancelled. The parent has been told.';
  if (await run(request, { success, error: 'Could not cancel the meeting.' })) {
    viewOpen.value = false;
    load();
  }
}

const rowActions = (row) => [
  { title: 'View details', icon: 'mdi-file-document-outline', onClick: () => view(row) },
  { title: 'Accept', icon: 'mdi-check', hidden: !canAnswer(row) || row.status === 'accepted', onClick: () => accept(row) },
  { title: 'Change date & time', icon: 'mdi-calendar-edit-outline', hidden: !canAnswer(row), onClick: () => openMove(row) },
  { title: 'Decline', icon: 'mdi-close', destructive: true, hidden: !canAnswer(row) || row.status === 'accepted', onClick: () => openDecline(row) },
  {
    title: 'Cancel meeting',
    icon: 'mdi-calendar-remove-outline',
    destructive: true,
    hidden: !isOpenRow(row) || (!sentByMe(row) && row.status !== 'accepted'),
    onClick: () => cancelMeeting(row),
  },
];
</script>

<template>
  <UiTableCard
    v-model:tab="tab"
    title="Appointments"
    :description="
      isPrefect
        ? 'Meetings you and the other Prefects asked parents to about discipline reports.'
        : 'Meetings parents asked you for, about their child\'s grades. Accept, decline, or suggest another time.'
    "
    :tabs="TABS"
    :headers="headers"
    :items="shown"
    :loading="loading"
    :error="error"
    search-placeholder="Search appointments"
    :empty-text="tab === 'pending' ? 'Nothing waiting for an answer.' : tab === 'cancelled' ? 'No cancelled appointments.' : 'No appointments here.'"
    row-label="name"
    :row-actions="rowActions"
  >
    <template #[`item.name`]="{ item }">
      <UiPerson
        :name="item.name"
        :subtitle="[senderText(item), aboutText(item)].filter(Boolean).join(' · ')"
      />
    </template>
    <template #[`item.kind`]="{ item }">
      <UiBadge :variant="kindOf(item).variant">{{ kindOf(item).text }}</UiBadge>
    </template>
    <template #[`item.purpose`]="{ item }">
      <span class="text-truncate d-inline-block" style="max-width: 320px">{{ item.purpose }}</span>
    </template>
    <template #[`item.schedule`]="{ item }">
      <span>{{ formatDateTime(item.schedule) }}</span>
      <span v-if="item.requestedSchedule" class="d-block text-medium-emphasis text-caption">{{ movedText(item) }}</span>
    </template>
    <template #[`item.status`]="{ item }">
      <UiBadge v-if="isPast(item) && item.status === 'pending'" variant="outline">Missed</UiBadge>
      <span v-else :title="cancelledText(item)">
        <UiBadge :variant="statusOf(item).variant">{{ statusOf(item).text }}</UiBadge>
      </span>
    </template>
  </UiTableCard>

  <UiDialog
    v-model="viewOpen"
    :title="viewing?.name ?? 'Appointment'"
    :description="viewing ? aboutText(viewing) : ''"
    :persistent="false"
  >
    <div v-if="viewing" class="form-stack">
      <div><UiBadge :variant="statusOf(viewing).variant">{{ statusOf(viewing).text }}</UiBadge></div>
      <UiDetailList :items="details" />
    </div>
    <template #footer="{ close }">
      <v-btn variant="outlined" @click="close">Close</v-btn>
      <v-btn
        v-if="viewing && sentByMe(viewing) && isOpenRow(viewing)"
        variant="outlined"
        @click="cancelMeeting(viewing)"
      >
        Cancel meeting
      </v-btn>
      <template v-if="viewing && canAnswer(viewing)">
        <v-btn v-if="viewing.status === 'accepted'" variant="outlined" @click="cancelMeeting(viewing)">Cancel meeting</v-btn>
        <v-btn v-else variant="outlined" @click="openDecline(viewing)">Decline</v-btn>
        <v-btn variant="outlined" @click="openMove(viewing)">Change date & time</v-btn>
        <v-btn v-if="viewing.status !== 'accepted'" color="primary" :loading="busy" @click="accept(viewing)">Accept</v-btn>
      </template>
    </template>
  </UiDialog>

  <UiFormDialog
    v-model="declineOpen"
    v-model:values="declineForm"
    title="Decline appointment"
    :description="answering ? `${answering.name} · ${formatDateTime(answering.schedule)}` : ''"
    :fields="DECLINE_FIELDS"
    :saving="busy"
    submit-text="Decline"
    @submit="respond(answering, { action: 'decline', note: declineForm.note }, 'Declined. The parent has been told.')"
  />

  <UiFormDialog
    v-model="moveOpen"
    v-model:values="moveForm"
    title="Change date & time"
    :description="answering ? `${answering.name} asked for ${formatDateTime(answering.requestedSchedule || answering.schedule)}` : ''"
    :fields="MOVE_FIELDS"
    layout="grid"
    :saving="busy"
    submit-text="Move and accept"
    @submit="
      respond(
        answering,
        { action: 'reschedule', schedule: `${moveForm.date} ${moveForm.time}:00`, note: moveForm.note },
        'Moved and accepted. The parent has been told.',
      )
    "
  />
</template>
